import { useState } from 'react';
import type { DealPerson } from './deal-kit';
import { Button, Select, Tabs, TextInput, Textarea } from '@forecastify/ui';
import { useT } from '../../i18n';
import { DateField } from '../date-field';
import { Refusal, type CardWrite } from './overview-parts';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D5 · the overview's composer.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *Jegyzet · Feladat · Következő lépés* in the mockup. ⚠️ **Hívás and E-mail are two more tabs**
 * (DM-6: calls and emails are logged by hand, there is no mailbox or dialler integration) — each
 * a subject, what was said and who was on it, landing in the feed under *Hívás / E-mail*. A
 * meeting is not composable here; the kind exists for the feed and the to-do list.
 *
 * ⚠️ *Következő lépés* is not a form of its own: choosing it opens the next-step dialog, so the
 * step has ONE editor — the same one the card's pencil opens.
 */

type Kind = 'note' | 'task' | 'call' | 'email';
type Tab = Kind | 'next';
const TABS: readonly Tab[] = ['note', 'task', 'call', 'email', 'next'];

function tabLabel(t: ReturnType<typeof useT>, tab: Tab): string {
  if (tab === 'task') return t('dealOverview.composer.task');
  if (tab === 'call') return t('dealOverview.composer.call');
  if (tab === 'email') return t('dealOverview.composer.email');
  if (tab === 'next') return t('dealOverview.composer.next');
  return t('dealOverview.composer.note');
}

function submitLabel(t: ReturnType<typeof useT>, kind: Kind): string {
  if (kind === 'task') return t('dealOverview.composer.saveTask');
  if (kind === 'call') return t('dealOverview.composer.saveCall');
  if (kind === 'email') return t('dealOverview.composer.saveEmail');
  return t('dealOverview.composer.saveNote');
}

export function OverviewComposer({
  members,
  defaultAssigneeId,
  write,
  onNextStep,
}: {
  readonly members: readonly DealPerson[];
  /** The reader's own member id — a task is usually one's own. */
  readonly defaultAssigneeId: string;
  readonly write: CardWrite;
  readonly onNextStep: () => void;
}) {
  const t = useT();
  const [kind, setKind] = useState<Kind>('note');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [participants, setParticipants] = useState('');
  const [due, setDue] = useState('');
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId);
  const [empty, setEmpty] = useState(false);

  const titled = kind !== 'note';
  const comm = kind === 'call' || kind === 'email';

  const submit = async (): Promise<void> => {
    if (title.trim() === '' && body.trim() === '') {
      setEmpty(true);
      return;
    }
    const ok = await write.run('/events', 'POST', {
      kind,
      title: titled ? title.trim() : '',
      body: body.trim(),
      ...(kind === 'task'
        ? {
            /* ⚠️ A calendar date, carried as noon UTC — the same convention the server uses when a
               finished next step becomes a task, so the date reads back unshifted everywhere. */
            dueAt: due === '' ? null : `${due}T12:00:00Z`,
            assigneeMemberId: assigneeId === '' ? null : assigneeId,
          }
        : {}),
      ...(comm ? { participants: participants.trim() } : {}),
    });
    if (!ok) return;
    setTitle('');
    setBody('');
    setParticipants('');
    setDue('');
  };

  return (
    <div className="bg-card flex flex-col gap-2 rounded-xl border p-3">
      <Tabs
        variant="pill"
        label={t('dealOverview.composer.label')}
        value={kind}
        onValueChange={(value) => {
          if (value === 'next') {
            onNextStep();
            return;
          }
          setKind(value as Kind);
          setEmpty(false);
        }}
        items={TABS.map((tab) => ({ value: tab, label: tabLabel(t, tab) }))}
      />
      {titled ? (
        <TextInput
          value={title}
          maxLength={300}
          aria-label={
            kind === 'task'
              ? t('dealOverview.composer.taskTitle')
              : kind === 'call'
                ? t('dealOverview.composer.callTitle')
                : t('dealOverview.composer.emailTitle')
          }
          placeholder={
            kind === 'task'
              ? t('dealOverview.composer.taskTitle')
              : kind === 'call'
                ? t('dealOverview.composer.callTitle')
                : t('dealOverview.composer.emailTitle')
          }
          onChange={(event) => {
            setTitle(event.target.value);
            setEmpty(false);
          }}
        />
      ) : null}
      <Textarea
        rows={2}
        maxLength={8000}
        value={body}
        aria-label={tabLabel(t, kind)}
        placeholder={
          kind === 'task'
            ? t('dealOverview.composer.taskBody')
            : comm
              ? t('dealOverview.composer.commBody')
              : t('dealOverview.composer.notePlaceholder')
        }
        onChange={(event) => {
          setBody(event.target.value);
          setEmpty(false);
        }}
      />
      {comm ? (
        <TextInput
          value={participants}
          maxLength={300}
          aria-label={t('dealOverview.composer.participants')}
          placeholder={t('dealOverview.composer.participants')}
          onChange={(event) => {
            setParticipants(event.target.value);
          }}
        />
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {kind === 'task' ? (
          <>
            <div className="w-[160px]">
              <DateField value={due} onChange={setDue} label={t('dealOverview.composer.due')} />
            </div>
            <div className="w-[200px]">
              <Select
                searchable
                searchPlaceholder={t('dealOverview.rail.searchPeople')}
                aria-label={t('dealOverview.composer.assignee')}
                value={assigneeId}
                onChange={(event) => {
                  setAssigneeId(event.target.value);
                }}
                options={[
                  { value: '', label: t('dealNext.nobody') },
                  ...members.map((member) => ({ value: member.id, label: member.displayName })),
                ]}
              />
            </div>
          </>
        ) : null}
        <div className="flex-1" />
        {empty ? (
          <span className="text-muted-foreground text-[11.5px]">
            {t('dealOverview.composer.empty')}
          </span>
        ) : null}
        <Button size="sm" disabled={write.busy} onClick={() => void submit()}>
          {submitLabel(t, kind)}
        </Button>
      </div>
      <Refusal error={write.error} />
    </div>
  );
}
